import { useCallback, useEffect, useState } from 'react';
import { errorMessage } from '../api/client';
import {
  amount,
  billNumberOf,
  isCancelRequested,
  isEdited,
  isVoided,
  paymentSummary,
  posApi,
  saleTotalOf,
  type PosSale,
  type TodaysSales,
} from '../api/pos';
import { billTime, money } from '../format';
import { useAuth } from '../auth/AuthContext';
import InvoiceView from '../components/InvoiceView';
import BillTitles from '../components/BillTitles';
import SalesReport from '../components/SalesReport';
import WholeShopToggle, { BillBy, useCoversWholeShop } from '../components/WholeShopToggle';

export default function TodaysBillsScreen() {
  const { can } = useAuth();
  const [data, setData] = useState<TodaysSales | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<PosSale | null>(null);
  const [report, setReport] = useState(false);
  // Unticked every time the screen opens: a staff member's own drawer is
  // what they come here to count.
  const [allStaff, setAllStaff] = useState(false);
  const wholeShop = useCoversWholeShop(allStaff);

  const load = useCallback(() => {
    setLoading(true);
    posApi
      .todaysSales(allStaff)
      .then((res) => {
        setData(res);
        setError(null);
      })
      .catch((err: unknown) => setError(errorMessage(err, 'Could not load today’s bills.')))
      .finally(() => setLoading(false));
  }, [allStaff]);

  useEffect(load, [load]);

  async function openBill(sale: PosSale) {
    // The list row may be a summary; fetch the full bill so the print view has
    // every line rather than whatever the list happened to include.
    try {
      const full = await posApi.saleById(sale.id);
      setOpen(full ?? sale);
    } catch {
      setOpen(sale);
    }
  }

  // Drawn above the loading and error states too, so the box can be unticked
  // again while a load is under way or has failed.
  const actions = (
    <div className="screen-actions">
      <WholeShopToggle checked={allStaff} onChange={setAllStaff} />
      {/* The cash-up counts the drawer; the report says which books emptied
          it. Both are wanted at the end of a shift, so the way to one is on
          the screen that shows the other. */}
      {can('pos.reports') && (
        <button type="button" className="btn" onClick={() => setReport(true)}>
          Take report
        </button>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="today">
        {actions}
        <p className="muted">Loading today’s bills…</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="today">
        {actions}
        <p className="error">{error}</p>
        <button type="button" className="btn" onClick={load}>
          Try again
        </button>
      </div>
    );
  }

  const bills = data?.bills ?? [];
  const summary = data?.summary;

  return (
    <div className="today">
      {actions}

      {summary && (
        <div className="cashup">
          <div>
            <span className="cashup-label">Bills</span>
            <strong>{summary.count}</strong>
          </div>
          <div>
            <span className="cashup-label">Cancelled</span>
            <strong>{summary.voided}</strong>
          </div>
          <div>
            {/* Copies, not bills. One bill can carry a dozen books, so "how
                many did we sell today" is a different number from the one
                beside it — and it is the one that gets checked against the
                shelf at closing. */}
            <span className="cashup-label">Books sold</span>
            <strong>{summary.quantity}</strong>
          </div>
          <div className="cashup-gross">
            <span className="cashup-label">Takings</span>
            <strong>{money(summary.gross)}</strong>
          </div>
        </div>
      )}

      {/* The drawer is short by this much until an admin cancels these bills:
          the money went back to the customer, but the bills still count in
          the takings above. Said here so the cash-up does not come out wrong
          with nobody knowing why. */}
      {!!summary?.cancelRequested && (
        <p className="cashup-note">
          {summary.cancelRequested === 1
            ? '1 bill is waiting for an admin to cancel it'
            : `${summary.cancelRequested} bills are waiting for an admin to cancel them`}
          {' — '}
          {money(amount(summary.cancelRequestedAmount ?? 0))} already handed back and still counted in
          the takings.
        </p>
      )}

      {bills.length === 0 ? (
        <p className="muted empty">No bills raised yet today.</p>
      ) : (
        <>
          <div className="bill-head" aria-hidden="true">
            <span>Bill</span>
            <span>Time</span>
            <span>Payment</span>
            <span className="bill-head-amount">Amount</span>
          </div>
          <ul className="bill-list">
            {bills.map((sale) => {
              const voided = isVoided(sale);
              return (
                <li key={sale.id}>
                  <button
                    type="button"
                    className={voided ? 'bill-row bill-void' : 'bill-row'}
                    onClick={() => openBill(sale)}
                  >
                    <span className="bill-no">
                      {billNumberOf(sale)}
                      {voided && <span className="void-tag">CANCELLED</span>}
                      {!voided && isCancelRequested(sale) && (
                        <span className="cancel-req-tag">CANCEL REQUESTED</span>
                      )}
                      {!voided && isEdited(sale) && (
                        <span className="edit-tag">EDITED</span>
                      )}
                    </span>
                    <span className="bill-time">
                      {billTime(sale.createdAt)}
                      {wholeShop && <BillBy name={sale.counterStaff?.fullName} />}
                    </span>
                    <span className="bill-method">{paymentSummary(sale) ?? ''}</span>
                    <span className="bill-total">{money(saleTotalOf(sale))}</span>
                    <BillTitles sale={sale} />
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}

      {open && (
        <InvoiceView
          sale={open}
          onClose={() => setOpen(null)}
          onEdited={(updated) => {
            // The bill on screen becomes the edited one straight away; the
            // list and the cash-up behind it are reloaded, because a
            // edit moves the takings this screen exists to count.
            setOpen(updated);
            load();
          }}
        />
      )}
      {/* No dates passed: the server reports today, which is the only period
          this screen is ever about. */}
      {report && <SalesReport onClose={() => setReport(false)} />}
    </div>
  );
}
